import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { cn } from "@/dentlab/src/lib/utils/cn";
import { UnreadIndicator } from "@/dentlab/src/components/chat/unread-indicator.component";

const MUTED_COLORS = [
	"#950952",
	"#5E0035",
	"#023618",
	"#005C69",
	"#BF8B85",
	"#AD8A64",
	"#721121",
	"#A5402D",
	"#A49E8D",
	"#504136",
	"#689689",
	"#272838",
	"#5D536B",
	"#7D6B91",
	"#347FC4",
	"#B98B82",
	"#37515F",
	"#9899A6",
	"#AA78A6",
	"#824670",
	"#A2708A",
	"#629460",
];

const getBackgroundColor = (name: string) => {
	let hash = 0;
	for (let i = 0; i < name.length; i++) {
		hash = name.charCodeAt(i) + ((hash << 5) - hash);
	}
	return MUTED_COLORS[Math.abs(hash % MUTED_COLORS.length)];
};

interface VendorSidebarItemProps {
	vendorOrgId: string;
	vendorName: string;
	expanded: boolean;
	onVendorInteraction?: () => void;
	onBeforeNavigate?: () => boolean;
	unreadCount?: number;
}

export const VendorSidebarItem: React.FC<VendorSidebarItemProps> = ({
	vendorOrgId,
	vendorName,
	expanded,
	onVendorInteraction,
	onBeforeNavigate,
	unreadCount,
}) => {
	const navigate = useNavigate();
	const { vendorId } = useParams();
	const isSelected = vendorId === vendorOrgId;

	const handleClick = () => {
		if (onBeforeNavigate && !onBeforeNavigate()) return;
		onVendorInteraction?.();
		navigate(`/vendor/${vendorOrgId}`);
	};

	const initials =
		vendorName
			.split(" ")
			.map((word) => word.charAt(0))
			.slice(0, 2)
			.join("")
			.toUpperCase() || "?";

	const bgColor = getBackgroundColor(vendorName);

	return (
		<div
			onClick={handleClick}
			className={cn(
				"flex items-center gap-2 py-1 rounded-md cursor-pointer transition-colors duration-200 text-gray-700",
				expanded ? "px-2 justify-start" : "justify-center px-0",
				isSelected ? "bg-stone-300 font-medium" : "hover:bg-stone-300"
			)}
			data-testid="vendor-sidebar-item"
			aria-label={`Fremdlabor ${vendorName}`}
		>
			<div className="relative flex-shrink-0">
				<div
					className="w-[22px] h-[22px] rounded-sm flex items-center justify-center text-white font-medium"
					style={{ backgroundColor: bgColor }}
				>
					<span style={{ fontSize: "11px", lineHeight: 1 }}>
						{initials}
					</span>
				</div>
				{!expanded && (
					<UnreadIndicator
						count={unreadCount}
						showCount={false}
						className="absolute -top-[1px] -right-[1px]"
					/>
				)}
			</div>
			{expanded && (
				<>
					<span className="text-sm truncate font-medium flex-1 min-w-0">
						{vendorName || "Unbekannt"}
					</span>
					<UnreadIndicator
						count={unreadCount}
						showCount={true}
						className="flex-shrink-0"
					/>
				</>
			)}
		</div>
	);
};
